import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdClose, MdOutlineDeleteOutline } from 'react-icons/md';
import { confirmAction } from './ui/feedback';
import {
  SOCIAL_PLATFORMS,
  socialPlatformLabel,
  deleteBusinessProfileMedia,
  fetchBusinessProfileMedia,
  normalizeBusinessEntityType,
  saveBusinessSocialLinks,
  saveBusinessTagline,
  saveBusinessYoutubeLinks,
  uploadBusinessLogo,
  uploadBusinessProfilePhoto,
} from './profileMediaApi';
import { setBusinessLogoUrl } from './entityContext';
import { notifyBusinessContextChanged } from './restaurantNav';
import './css/KitchenMediaEditor.css';

const DEFAULT_LIMITS = {
  maxImages: 2,
  minImages: 0,
  maxYoutube: 2,
  minYoutube: 0,
  imagesRequired: false,
  youtubeRequired: false,
  label: 'Business',
};

function emptySocialInputs() {
  return SOCIAL_PLATFORMS.reduce((acc, platform) => {
    acc[platform] = '';
    return acc;
  }, {});
}

function syncLogoFromPayload(payload) {
  const logoUrl =
    payload?.logoUrl ||
    payload?.logo?.mediaUrl ||
    null;
  setBusinessLogoUrl(logoUrl);
  notifyBusinessContextChanged();
}

function applyPayload(payload, setters) {
  const {
    setPhotos,
    setYoutubeInputs,
    setSocialInputs,
    setTagline,
    setLogo,
    setLimits,
    setMediaRequired,
  } = setters;
  const photoRows = Array.isArray(payload?.photos)
    ? payload.photos
    : Array.isArray(payload?.kitchenPhotos)
      ? payload.kitchenPhotos
      : [];
  setPhotos(photoRows);
  const videos = Array.isArray(payload?.youtubeVideos) ? payload.youtubeVideos : [];
  const urls = videos.map((v) => v.mediaUrl || '').filter(Boolean);
  const maxY = Number(payload?.limits?.maxYoutube ?? 2) || 2;
  while (urls.length < Math.min(2, maxY)) urls.push('');
  setYoutubeInputs(urls.slice(0, maxY));

  const nextSocial = emptySocialInputs();
  const links = Array.isArray(payload?.socialLinks) ? payload.socialLinks : [];
  links.forEach((link) => {
    const platform = String(link.platform || '').toLowerCase();
    if (platform in nextSocial) {
      nextSocial[platform] = link.url || '';
    }
  });
  setSocialInputs(nextSocial);
  setTagline(String(payload?.tagline || '').trim());

  const logo =
    payload?.logo ||
    (payload?.logoUrl ? { mediaUrl: payload.logoUrl } : null);
  setLogo(logo);
  syncLogoFromPayload(payload);

  setLimits({ ...DEFAULT_LIMITS, ...(payload?.limits || {}) });
  setMediaRequired(Boolean(payload?.mediaRequired));
}

/**
 * Shared media editor for any business entity type (RESTAURANT | STORE | PHARMA | …).
 */
export default function KitchenMediaEditor({
  entityType = 'restaurant',
  entityId,
  canEdit = false,
}) {
  const businessType = normalizeBusinessEntityType(entityType);
  const [photos, setPhotos] = useState([]);
  const [logo, setLogo] = useState(null);
  const [pendingLogo, setPendingLogo] = useState(null);
  const [youtubeInputs, setYoutubeInputs] = useState(['', '']);
  const [socialInputs, setSocialInputs] = useState(emptySocialInputs);
  const [tagline, setTagline] = useState('');
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [mediaRequired, setMediaRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingVideos, setSavingVideos] = useState(false);
  const [savingSocial, setSavingSocial] = useState(false);
  const [savingTagline, setSavingTagline] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [editing, setEditing] = useState(false);

  const setters = useMemo(
    () => ({
      setPhotos,
      setYoutubeInputs,
      setSocialInputs,
      setTagline,
      setLogo,
      setLimits,
      setMediaRequired,
    }),
    [],
  );

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const payload = await fetchBusinessProfileMedia(businessType, entityId);
      applyPayload(payload, setters);
    } catch (err) {
      console.error('Profile media load failed', err);
      applyPayload({}, setters);
    } finally {
      setLoading(false);
    }
  }, [businessType, entityId, setters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      pendingFiles.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      if (pendingLogo?.preview) URL.revokeObjectURL(pendingLogo.preview);
    };
  }, [pendingFiles, pendingLogo]);

  const remainingPhotoSlots = Math.max(0, limits.maxImages - photos.length - pendingFiles.length);
  const photoLabel = `${limits.label || 'Business'} photos`;
  const requiredHint = mediaRequired
    ? 'Required in production'
    : 'Optional in this environment (required in production where configured)';
  const filledSocialCount = SOCIAL_PLATFORMS.filter((p) =>
    String(socialInputs[p] || '').trim(),
  ).length;

  const handlePickLogo = (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file) return;
    if (pendingLogo?.preview) URL.revokeObjectURL(pendingLogo.preview);
    setPendingLogo({ file, preview: URL.createObjectURL(file) });
  };

  const handleUploadLogo = async () => {
    if (!pendingLogo?.file || !entityId) return;
    setUploadingLogo(true);
    try {
      const payload = await uploadBusinessLogo(businessType, entityId, pendingLogo.file);
      applyPayload(payload, setters);
      if (pendingLogo.preview) URL.revokeObjectURL(pendingLogo.preview);
      setPendingLogo(null);
      window.alert('Logo uploaded.');
    } catch (err) {
      window.alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Logo upload failed.',
      );
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!logo?.mediaId) return;
    const ok = await confirmAction({
      title: 'Delete logo?',
      message: 'Remove this logo from the business profile and left nav?',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      const payload = await deleteBusinessProfileMedia(businessType, entityId, logo.mediaId);
      applyPayload(payload, setters);
    } catch (err) {
      window.alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Could not delete logo.',
      );
    }
  };

  const handlePickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setPendingFiles((current) => {
      const slots = Math.max(0, limits.maxImages - photos.length - current.length);
      if (slots <= 0) {
        window.alert(`Maximum ${limits.maxImages} photos allowed.`);
        return current;
      }
      const accepted = files.slice(0, slots).map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      return [...current, ...accepted];
    });
  };

  const handleUpload = async () => {
    if (!pendingFiles.length || !entityId) return;
    setUploading(true);
    try {
      let payload = null;
      for (const item of pendingFiles) {
        payload = await uploadBusinessProfilePhoto(businessType, entityId, item.file);
      }
      if (payload) applyPayload(payload, setters);
      pendingFiles.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
      setPendingFiles([]);
      window.alert('Photos uploaded.');
    } catch (err) {
      window.alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Photo upload failed.',
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (mediaId) => {
    const ok = await confirmAction({
      title: 'Delete photo?',
      message: 'Remove this photo from the customer profile?',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      const payload = await deleteBusinessProfileMedia(businessType, entityId, mediaId);
      applyPayload(payload, setters);
    } catch (err) {
      window.alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Could not delete photo.',
      );
    }
  };

  const handleSaveYoutube = async () => {
    const urls = youtubeInputs.map((u) => String(u || '').trim()).filter(Boolean);
    if (limits.youtubeRequired && urls.length < limits.minYoutube) {
      window.alert(`Add at least ${limits.minYoutube} YouTube link(s).`);
      return;
    }
    if (urls.length > limits.maxYoutube) {
      window.alert(`Maximum ${limits.maxYoutube} YouTube links.`);
      return;
    }
    setSavingVideos(true);
    try {
      const payload = await saveBusinessYoutubeLinks(businessType, entityId, urls);
      applyPayload(payload, setters);
      window.alert('YouTube links saved.');
    } catch (err) {
      window.alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Could not save YouTube links.',
      );
    } finally {
      setSavingVideos(false);
    }
  };

  const handleSaveSocial = async () => {
    const socialLinks = SOCIAL_PLATFORMS.reduce((acc, platform) => {
      const url = String(socialInputs[platform] || '').trim();
      if (url) acc[platform] = url;
      return acc;
    }, {});
    setSavingSocial(true);
    try {
      const payload = await saveBusinessSocialLinks(businessType, entityId, socialLinks);
      applyPayload(payload, setters);
      window.alert('Social links saved.');
    } catch (err) {
      window.alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Could not save social links.',
      );
    } finally {
      setSavingSocial(false);
    }
  };

  const handleSaveTagline = async () => {
    setSavingTagline(true);
    try {
      const payload = await saveBusinessTagline(businessType, entityId, tagline);
      applyPayload(payload, setters);
      window.alert('Tagline saved.');
    } catch (err) {
      window.alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Could not save tagline.',
      );
    } finally {
      setSavingTagline(false);
    }
  };

  if (!entityId) {
    return <p className="kitchen-media-editor__hint">Save the profile first to add media.</p>;
  }

  return (
    <div className="kitchen-media-editor">
      <div className="kitchen-media-editor__intro">
        <p className="kitchen-media-editor__hint">
          Customers see these photos, videos, social links, about text, and items on the business
          profile. Shared media storage works for restaurant, store, pharma, and more. Files are on
          the server now (Drive/S3 later). {requiredHint}.
        </p>
        <div className="kitchen-media-editor__intro-actions">
          {canEdit && !editing ? (
            <button
              type="button"
              className="eats-admin-header-btn kitchen-media-editor__edit-btn"
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
          ) : null}
          {canEdit && editing ? (
            <button
              type="button"
              className="eats-admin-header-btn kitchen-media-editor__edit-btn"
              onClick={() => {
                setEditing(false);
                setPendingFiles([]);
                if (pendingLogo?.preview) URL.revokeObjectURL(pendingLogo.preview);
                setPendingLogo(null);
                load();
              }}
            >
              Done
            </button>
          ) : null}
        </div>
      </div>

      {loading ? <p className="kitchen-media-editor__hint">Loading media…</p> : null}

      <section className="kitchen-media-editor__section">
        <h4 className="kitchen-media-editor__title">
          Business logo <span>(shown in left nav · optional)</span>
        </h4>
        <div className="kitchen-media-editor__grid">
          {logo?.mediaUrl ? (
            <div className="kitchen-media-editor__card kitchen-media-editor__card--logo">
              <img src={logo.mediaUrl} alt="Business logo" />
              {canEdit && editing && logo.mediaId ? (
                <button
                  type="button"
                  className="kitchen-media-editor__icon-btn"
                  aria-label="Delete logo"
                  onClick={handleDeleteLogo}
                >
                  <MdOutlineDeleteOutline />
                </button>
              ) : null}
              <a
                className="kitchen-media-editor__view-link"
                href={logo.mediaUrl}
                target="_blank"
                rel="noreferrer"
              >
                View
              </a>
            </div>
          ) : null}
          {pendingLogo ? (
            <div className="kitchen-media-editor__card kitchen-media-editor__card--logo is-pending">
              <img src={pendingLogo.preview} alt="Pending logo" />
              {editing ? (
                <button
                  type="button"
                  className="kitchen-media-editor__icon-btn"
                  aria-label="Remove pending logo"
                  onClick={() => {
                    if (pendingLogo.preview) URL.revokeObjectURL(pendingLogo.preview);
                    setPendingLogo(null);
                  }}
                >
                  <MdClose />
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        {canEdit && editing ? (
          <div className="kitchen-media-editor__actions">
            <label className="kitchen-media-editor__file">
              <input type="file" accept="image/*" onChange={handlePickLogo} />
              Choose logo
            </label>
            {pendingLogo ? (
              <button
                type="button"
                className="eats-admin-header-btn"
                disabled={uploadingLogo}
                onClick={handleUploadLogo}
              >
                {uploadingLogo ? 'Uploading…' : 'Upload logo'}
              </button>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="kitchen-media-editor__section">
        <h4 className="kitchen-media-editor__title">
          {photoLabel}{' '}
          <span>
            ({photos.length}/{limits.maxImages}
            {limits.imagesRequired ? ' · required' : ''})
          </span>
        </h4>
        <div className="kitchen-media-editor__grid">
          {photos.map((photo) => (
            <div key={photo.mediaId} className="kitchen-media-editor__card">
              <img src={photo.mediaUrl} alt={photo.mediaName || 'Photo'} />
              {canEdit && editing ? (
                <button
                  type="button"
                  className="kitchen-media-editor__icon-btn"
                  aria-label="Delete photo"
                  onClick={() => handleDeletePhoto(photo.mediaId)}
                >
                  <MdOutlineDeleteOutline />
                </button>
              ) : null}
              {photo.mediaUrl ? (
                <a
                  className="kitchen-media-editor__view-link"
                  href={photo.mediaUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  View
                </a>
              ) : null}
            </div>
          ))}
          {pendingFiles.map((item, index) => (
            <div key={`pending-${index}`} className="kitchen-media-editor__card is-pending">
              <img src={item.preview} alt="Pending upload" />
              {editing ? (
                <button
                  type="button"
                  className="kitchen-media-editor__icon-btn"
                  aria-label="Remove pending"
                  onClick={() => {
                    setPendingFiles((current) => {
                      const next = current.filter((_, i) => i !== index);
                      if (current[index]?.preview) URL.revokeObjectURL(current[index].preview);
                      return next;
                    });
                  }}
                >
                  <MdClose />
                </button>
              ) : null}
            </div>
          ))}
        </div>
        {photos.length > 0 ? (
          <ul className="kitchen-media-editor__link-list">
            {photos.map((photo, index) => (
              <li key={`link-${photo.mediaId || index}`}>
                <a href={photo.mediaUrl} target="_blank" rel="noreferrer">
                  Photo {index + 1}
                  {photo.mediaName ? ` · ${photo.mediaName}` : ''}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="kitchen-media-editor__hint">
            No kitchen/store photos uploaded yet. Use Edit media → Choose photos.
          </p>
        )}
        {canEdit && editing && remainingPhotoSlots > 0 ? (
          <div className="kitchen-media-editor__actions">
            <label className="kitchen-media-editor__file">
              <input type="file" accept="image/*" multiple onChange={handlePickFiles} />
              Choose photos
            </label>
            {pendingFiles.length > 0 ? (
              <button
                type="button"
                className="eats-admin-header-btn"
                disabled={uploading}
                onClick={handleUpload}
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="kitchen-media-editor__section">
        <h4 className="kitchen-media-editor__title">
          YouTube videos{' '}
          <span>
            ({youtubeInputs.filter(Boolean).length}/{limits.maxYoutube}
            {limits.youtubeRequired
              ? ' · required'
              : businessType === 'RESTAURANT'
                ? ' · 1–2 in production'
                : ' · optional'})
          </span>
        </h4>
        <div className="kitchen-media-editor__youtube-list">
          {youtubeInputs.map((value, index) => (
            <div key={`yt-${index}`} className="kitchen-media-editor__youtube-row">
              <label htmlFor={`yt-input-${businessType}-${index}`}>Link {index + 1}</label>
              <input
                id={`yt-input-${businessType}-${index}`}
                type="url"
                placeholder="https://www.youtube.com/watch?v=…"
                value={value}
                disabled={!canEdit || !editing}
                onChange={(e) => {
                  const next = [...youtubeInputs];
                  next[index] = e.target.value;
                  setYoutubeInputs(next);
                }}
              />
            </div>
          ))}
        </div>
        {canEdit && editing ? (
          <div className="kitchen-media-editor__actions">
            <button
              type="button"
              className="eats-admin-header-btn"
              disabled={savingVideos}
              onClick={handleSaveYoutube}
            >
              {savingVideos ? 'Saving…' : 'Save YouTube links'}
            </button>
          </div>
        ) : null}
      </section>

      <section className="kitchen-media-editor__section">
        <h4 className="kitchen-media-editor__title">
          Tagline <span>(optional · shown under business name)</span>
        </h4>
        <div className="kitchen-media-editor__youtube-list">
          <div className="kitchen-media-editor__youtube-row">
            <label htmlFor={`tagline-${businessType}-${entityId}`}>Tagline</label>
            <input
              id={`tagline-${businessType}-${entityId}`}
              type="text"
              maxLength={120}
              placeholder="e.g. Fresh meals from our kitchen"
              value={tagline}
              disabled={!canEdit || !editing}
              onChange={(e) => setTagline(e.target.value)}
            />
          </div>
        </div>
        {canEdit && editing ? (
          <div className="kitchen-media-editor__actions">
            <button
              type="button"
              className="eats-admin-header-btn"
              disabled={savingTagline}
              onClick={handleSaveTagline}
            >
              {savingTagline ? 'Saving…' : 'Save tagline'}
            </button>
          </div>
        ) : null}
      </section>

      <section className="kitchen-media-editor__section">
        <h4 className="kitchen-media-editor__title">
          Social links to follow <span>({filledSocialCount} set · optional)</span>
        </h4>
        <div className="kitchen-media-editor__youtube-list">
          {SOCIAL_PLATFORMS.map((platform) => (
            <div key={platform} className="kitchen-media-editor__youtube-row">
              <label htmlFor={`social-${businessType}-${platform}`}>
                {socialPlatformLabel(platform)}
              </label>
              <input
                id={`social-${businessType}-${platform}`}
                type="url"
                placeholder={
                  platform === 'website'
                    ? 'https://crltgroup.com'
                    : 'https://…'
                }
                value={socialInputs[platform] || ''}
                disabled={!canEdit || !editing}
                onChange={(e) => {
                  setSocialInputs((current) => ({
                    ...current,
                    [platform]: e.target.value,
                  }));
                }}
              />
            </div>
          ))}
        </div>
        {canEdit && editing ? (
          <div className="kitchen-media-editor__actions">
            <button
              type="button"
              className="eats-admin-header-btn"
              disabled={savingSocial}
              onClick={handleSaveSocial}
            >
              {savingSocial ? 'Saving…' : 'Save social links'}
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
